import { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Button, Spinner } from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import { getBuildingInspectionById } from './FormikHelpers/Api';

const BuildingInspectionPDFTemplate = ({ data }) => {
    const sectionTitles = {
        interior: 'INTERIOR AREAS',
        elevators: 'ELEVATORS',
        underground: 'UNDERGROUND AREAS',
        outside: 'EXTERIOR & GROUNDS',
        misc_others: 'MISC / OTHERS',
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    return (
        <div
            id="pdf-template"
            style={{
                position: 'absolute',
                left: '-9999px',
                top: 0,
                width: '210mm',
                padding: '20mm',
                backgroundColor: '#ffffff',
                fontFamily: 'Arial, sans-serif',
                fontSize: '11pt',
                lineHeight: '1.5',
                color: '#000000',
            }}
        >
            {/* Header */}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px 15px',
                    backgroundColor: '#0D568F',
                    marginBottom: '20px',
                    borderRadius: '5px',
                }}
            >
                <h5 style={{ margin: 0, color: '#ffffff', fontSize: '16pt', fontWeight: 'bold' }}>
                    Common Area Inspection
                </h5>
                <img src="/images/rentdigicare-logo.svg" alt="logo" style={{ height: '34px' }} />
            </div>

            {/* Inspection Date */}
            <div style={{ marginBottom: '25px' }}>
                <label style={{ fontWeight: 'bold', fontSize: '12pt', color: '#000' }}>
                    Inspection Date
                </label>
                <div
                    style={{
                        marginTop: '5px',
                        padding: '8px',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        backgroundColor: '#f8f9fa',
                    }}
                >
                    {formatDate(data?.inspection_date)}
                </div>
            </div>

            {/* Property Info */}
            <div
                style={{
                    marginBottom: '25px',
                    backgroundColor: '#f8f9fa',
                    padding: '15px',
                    borderRadius: '5px',
                    border: '1px solid #dee2e6',
                }}
            >
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                        <tr>
                            <td style={{ padding: '5px 10px', fontWeight: 'bold', width: '30%' }}>Property:</td>
                            <td style={{ padding: '5px 10px' }}>
                                {data?.property_details?.property_id?.title || data?.property_details?.title || 'N/A'}
                            </td>
                        </tr>
                        <tr>
                            <td style={{ padding: '5px 10px', fontWeight: 'bold' }}>Address:</td>
                            <td style={{ padding: '5px 10px' }}>
                                {data?.property_details?.building_name || data?.property_details?.address || 'N/A'}
                            </td>
                        </tr>
                        <tr>
                            <td style={{ padding: '5px 10px', fontWeight: 'bold' }}>Report Generated:</td>
                            <td style={{ padding: '5px 10px' }}>{formatDate(new Date())}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Sections */}
            {Object.entries(sectionTitles).map(([key, title]) => {
                const items = data?.[key] || [];
                if (!items.length) return null;

                return (
                    <div key={key} style={{ marginBottom: '25px', pageBreakInside: 'avoid' }}>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '8px 12px',
                                backgroundColor: '#f1f5f9',
                                borderRadius: '4px',
                                marginBottom: '10px',
                            }}
                        >
                            <h6 style={{ margin: 0, fontWeight: 'bold', fontSize: '13pt', color: '#0D568F' }}>
                                {title}
                                <span
                                    style={{
                                        backgroundColor: '#0D568F',
                                        color: '#fff',
                                        padding: '2px 6px',
                                        borderRadius: '10px',
                                        fontSize: '9pt',
                                        marginLeft: '8px',
                                    }}
                                >
                                    {items.length}
                                </span>
                            </h6>
                        </div>

                        <table
                            style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                marginBottom: '10px',
                                fontSize: '9pt',
                            }}
                        >
                            <thead>
                                <tr style={{ backgroundColor: '#e9ecef' }}>
                                    <th style={{ border: '1px solid #dee2e6', padding: '8px', textAlign: 'left', fontWeight: 'bold' }}>
                                        CATEGORY
                                    </th>
                                    <th
                                        style={{
                                            border: '1px solid #dee2e6',
                                            padding: '8px',
                                            textAlign: 'center',
                                            fontWeight: 'bold',
                                            width: '80px',
                                        }}
                                    >
                                        NEEDS WORK
                                    </th>
                                    <th
                                        style={{
                                            border: '1px solid #dee2e6',
                                            padding: '8px',
                                            textAlign: 'center',
                                            fontWeight: 'bold',
                                            width: '100px',
                                        }}
                                    >
                                        DUE DATE
                                    </th>
                                    <th
                                        style={{
                                            border: '1px solid #dee2e6',
                                            padding: '8px',
                                            textAlign: 'center',
                                            fontWeight: 'bold',
                                            width: '80px',
                                        }}
                                    >
                                        STATUS
                                    </th>
                                    <th style={{ border: '1px solid #dee2e6', padding: '8px', textAlign: 'left', fontWeight: 'bold' }}>
                                        NOTES
                                    </th>
                                    <th
                                        style={{
                                            border: '1px solid #dee2e6',
                                            padding: '8px',
                                            textAlign: 'center',
                                            fontWeight: 'bold',
                                            width: '80px',
                                        }}
                                    >
                                        ATTACHMENTS
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item, idx) => (
                                    <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f8f9fa' }}>
                                        <td style={{ border: '1px solid #dee2e6', padding: '8px' }}>{item.name || '-'}</td>
                                        <td style={{ border: '1px solid #dee2e6', padding: '8px', textAlign: 'center' }}>
                                            {item.needs_work === false ? (
                                                <span style={{ color: '#dc3545', fontWeight: 'bold' }}>Yes</span>
                                            ) : (
                                                <span style={{ color: '#28a745' }}>No</span>
                                            )}
                                        </td>
                                        <td style={{ border: '1px solid #dee2e6', padding: '8px', textAlign: 'center' }}>
                                            {item.due_date ? formatDate(item.due_date) : '-'}
                                        </td>
                                        <td style={{ border: '1px solid #dee2e6', padding: '8px', textAlign: 'center' }}>
                                            {item.needs_work === false ? (
                                                item.completed ? (
                                                    <span style={{ color: '#28a745', fontWeight: 'bold' }}>Done</span>
                                                ) : (
                                                    <span style={{ color: '#ffc107', fontWeight: 'bold' }}>Pending</span>
                                                )
                                            ) : (
                                                '-'
                                            )}
                                        </td>
                                        <td style={{ border: '1px solid #dee2e6', padding: '8px' }}>{item.comments || '-'}</td>
                                        <td style={{ border: '1px solid #dee2e6', padding: '8px', textAlign: 'center' }}>
                                            {item.attachments?.length > 0 ? <strong>{item.attachments.length}</strong> : '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );
            })}

            {/* Conclusions & Additional Notes */}
            <div style={{ marginBottom: '20px' }}>
                <label style={{ fontWeight: 'bold', fontSize: '12pt', color: '#000' }}>
                    Conclusions & Recommendations
                </label>
                <div
                    style={{
                        marginTop: '5px',
                        padding: '12px',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        backgroundColor: '#f8f9fa',
                        minHeight: '80px',
                        whiteSpace: 'pre-wrap',
                    }}
                >
                    {data?.conclusions_recommendations || 'N/A'}
                </div>
            </div>
            {/* Footer */}
            <div
                style={{
                    marginTop: '40px',
                    paddingTop: '15px',
                    borderTop: '2px solid #dee2e6',
                    fontSize: '8pt',
                    color: '#666',
                    textAlign: 'center',
                }}
            >
                <p style={{ margin: '5px 0' }}>
                    This is a computer-generated report. For digital attachments, please refer to the online inspection record.
                </p>
                <p style={{ margin: '5px 0' }}>Generated on {new Date().toLocaleString('en-US')}</p>
            </div>
        </div>
    );
};

export const DownloadInspectionPDFButton = ({ inspectionId, disabled = false }) => {
    const [generating, setGenerating] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!inspectionId) return;

        let isMounted = true;

        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const inspectionData = await getBuildingInspectionById(inspectionId);
               if (isMounted) setData(inspectionData);
            } catch (err) {
                if (isMounted) setError('Failed to load inspection data');
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchData();

        return () => {
            isMounted = false;
        };
    }, [inspectionId]);

    const generatePDF = async () => {
        if (!data) return;

        setGenerating(true);

        try {
            const container = document.createElement('div');
            document.body.appendChild(container);

            ReactDOM.render(<BuildingInspectionPDFTemplate data={data} />, container);
            await new Promise((resolve) => setTimeout(resolve, 600));

            const element = document.getElementById('pdf-template');
            if (!element) throw new Error('Template element not found');

            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
                windowWidth: element.scrollWidth,
            });

            const imgWidth = 210;
            const pageHeight = 297;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            const pdf = new jsPDF('p', 'mm', 'a4');
            let heightLeft = imgHeight;
            let position = 0;

            pdf.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;

            while (heightLeft > 0) {
                position = heightLeft - imgHeight;
                pdf.addPage();
                pdf.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', 0, position, imgWidth, imgHeight);
                heightLeft -= pageHeight;
            }

            const propertyName = data?.property_details?.property_name || 'Property';
            const dateStr = data?.inspection_date
                ? new Date(data.inspection_date).toISOString().split('T')[0]
                : new Date().toISOString().split('T')[0];

            const filename = `Inspection_${propertyName.replace(/[^a-z0-9]/gi, '_')}_${dateStr}.pdf`;

            pdf.save(filename);

            // Cleanup
            ReactDOM.unmountComponentAtNode(container);
            document.body.removeChild(container);
        } catch (err) {
            console.error('PDF generation failed:', err);
            alert('Failed to generate PDF. Please try again.');
        } finally {
            setGenerating(false);
        }
    };

    // ─── Render ──────────────────────────────────────────────────────
    if (loading) {
        return (
            <Button color="secondary" disabled>
                <Spinner size="sm" /> Loading data...
            </Button>
        );
    }

    if (error || !inspectionId) {
        return (
            <Button color="danger" disabled>
                Cannot generate PDF
            </Button>
        );
    }

    return (
        <Button
            color="success"
            onClick={generatePDF}
            disabled={disabled || generating || !data}
            className="d-flex align-items-center"
        >
            {generating ? (
                <>
                    <Spinner size="sm" className="me-2" />
                    Generating PDF...
                </>
            ) : (
                <>
                    <FeatherIcon icon="download" size={16} className="me-2" />
                    Download PDF Report
                </>
            )}
        </Button>
    );
};